'use client'

import type { MouseEvent } from 'react'
import Link from 'next/link'
import { ChevronLeft } from 'lucide-react'

import { useRouter } from 'next/navigation'
import { useListBack, useListReturnHref, usePreviousOutsidePeople } from '@/hooks/use-list-return'
import { useTaskBack } from '@/components/encuentro/page/task-back'
import type { Destination } from '@/lib/navigation/routes'
import { cn } from '@/lib/utils'

/**
 * Where "‹ Eventos" goes. A destination uses the return to lists of F8
 * (docs/redesign/README.md §3): history back when the previous entry is that
 * list, otherwise the remembered list with its filters, otherwise the
 * canonical one. A plain `href` is for pages outside the five destinations.
 */
export type PageBack =
  | ({ label: string } & (
      | { destination: Destination; href?: never; task?: never }
      | { href: string; destination?: never; task?: never }
    ))
  /**
   * A task page (lib/navigation/task-pages.ts): "‹ …" names and returns to
   * the page it was opened from, with its scroll (`useTaskReturn`).
   */
  | { task: true; label?: never; destination?: never; href?: never }

const backClass = cn(
  'inline-flex min-h-en-control w-fit items-center gap-1 rounded-en-md -ml-2 px-2',
  'font-en-sans text-en-label text-en-ink-muted no-underline',
  'transition-colors duration-(--en-dur-feedback) ease-(--en-ease) motion-reduce:transition-none',
  'hover:bg-en-surface-sunken hover:text-en-ink',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus'
)

function ListBackLink({
  destination,
  label,
  className,
}: {
  destination: Destination
  label: string
  className?: string
}) {
  const back = useListBack(destination)
  const href = useListReturnHref(destination)

  // Outside an org there is no list to return to.
  if (!href) return null

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // A modified click (new tab, new window) keeps the plain link.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return
    }
    event.preventDefault()
    back()
  }

  return (
    <Link href={href} onClick={onClick} className={cn(backClass, className)}>
      <ChevronLeft aria-hidden className="size-5 shrink-0" />
      {label}
    </Link>
  )
}

function TaskBackLink({ className }: { className?: string }) {
  const { href, text, back } = useTaskBack()
  if (!href || !text) return null

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return
    }
    event.preventDefault()
    back()
  }

  return (
    <Link href={href} onClick={onClick} className={cn(backClass, className)}>
      <ChevronLeft aria-hidden className="size-5 shrink-0" />
      {text}
    </Link>
  )
}

/** The back link above a page title. */
export function BackLink({ back, className }: { back: PageBack; className?: string }) {
  if (back.task) return <TaskBackLink className={className} />
  if (back.destination) {
    return <ListBackLink destination={back.destination} label={back.label} className={className} />
  }
  return (
    <Link href={back.href} className={cn(backClass, className)}>
      <ChevronLeft aria-hidden className="size-5 shrink-0" />
      {back.label}
    </Link>
  )
}

/**
 * A profile's back link: "‹ Personas" by default; "‹ Volver" to the page the
 * member came from (a post, a group, an event, a job) when it was not People.
 */
export function ProfileBackLink({
  peopleLabel,
  backLabel,
  className,
}: {
  peopleLabel: string
  backLabel: string
  className?: string
}) {
  const router = useRouter()
  const previous = usePreviousOutsidePeople()
  if (!previous) {
    return <BackLink back={{ label: peopleLabel, destination: 'people' }} className={className} />
  }
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return
    }
    event.preventDefault()
    router.back()
  }
  return (
    <Link href={previous} onClick={onClick} className={cn(backClass, className)}>
      <ChevronLeft aria-hidden className="size-5 shrink-0" />
      {backLabel}
    </Link>
  )
}
